/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useState, useEffect, useCallback} from 'react';

/**
 * A reusable hook that calculates the available height based on the element’s
 * top offset and the current window height.
 *
 * @param ref - A ref to the container element
 * @param extraPadding - Optional extra space (in pixels) to subtract from the available height (default: 48)
 * @returns An object containing:
 *   - `height`: The computed available height in pixels.
 *   - `updateHeight`: A function to manually trigger a height update.
 */

export function useDynamicHeight(
  ref: React.RefObject<HTMLElement>,
  extraPadding: number = 3 * 16, // 3 times MUI standard unit
): {height: number; updateHeight: () => void} {
  const [height, setHeight] = useState(0);

  const updateHeight = useCallback(() => {
    if (ref.current) {
      const top = ref.current.getBoundingClientRect().top;
      const availableHeight = window.innerHeight - top - extraPadding;
      setHeight(availableHeight);
    }
  }, [ref, extraPadding]);

  useEffect(() => {
    updateHeight(); // Initial calculation
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [updateHeight]);

  return {height, updateHeight};
}
